import React, { useState, useEffect, useRef, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Grid,
  Button,
  TextField,
  MenuItem,
  Typography,
  Divider,
  Chip,
  IconButton,
  Tooltip,
  InputAdornment,
  Collapse,
} from "@mui/material";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";

import InstagramIcon from "@mui/icons-material/Instagram";
import TwitterIcon from "@mui/icons-material/Twitter";
import FacebookIcon from "@mui/icons-material/Facebook";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSnapchatGhost, faTiktok } from "@fortawesome/free-brands-svg-icons";
import LinkIcon from "@mui/icons-material/Link";

const buildDefaultState = (SectionToFormFields) => {
  const defaultValues = {};
  Object.keys(SectionToFormFields).forEach((sectionName) => {
    defaultValues[sectionName] = {};
    SectionToFormFields[sectionName].forEach((field, index) => {
      defaultValues[sectionName][`${sectionName}-section-field-${index}`] =
        field.defaultValue ?? "";
    });
  });
  return defaultValues;
};

const toCamelCase = (str) => {
  if (!str) return "";
  if (str.indexOf(" ") === -1) return str.toLowerCase();
  return str
    .toLowerCase()
    .replace(/[^a-zA-Z0-9]+(.)/g, (_, chr) => chr.toUpperCase());
};

const extractFormData = (currentFormState, sectionToFormFieldsRef) => {
  const formData = {};

  Object.keys(currentFormState).forEach((sectionName) => {
    formData[sectionName] = {};
    Object.keys(currentFormState[sectionName]).forEach((key) => {
      const fieldIndex = key.split("-section-field-")[1];
      const fieldData = sectionToFormFieldsRef.current?.[sectionName]?.[fieldIndex];
      if (!fieldData) return;
      const formattedKey = toCamelCase(fieldData.label);
      formData[sectionName][formattedKey] = currentFormState[sectionName][key];
    });
  });

  return formData;
};

const isSocialFieldLabel = (label = "") => {
  const l = label.toLowerCase();
  return (
    l.includes("instagram") ||
    l.includes("twitter") ||
    l.includes("x ") ||
    l === "x" ||
    l.includes("facebook") ||
    l.includes("snapchat") ||
    l.includes("tiktok") ||
    l.includes("tik tok")
  );
};

const socialConfigForLabel = (label = "") => {
  const l = label.toLowerCase();

  if (l.includes("instagram")) {
    return {
      key: "instagram",
      tooltip: "Instagram",
      icon: <InstagramIcon fontSize="small" />,
      buildUrl: (v) => `https://instagram.com/${String(v).trim().replace(/^@/, "")}`,
    };
  }
  if (l.includes("twitter") || l === "x" || l.includes(" x ")) {
    return {
      key: "twitter",
      tooltip: "X",
      icon: <TwitterIcon fontSize="small" />,
      buildUrl: (v) => `https://twitter.com/${String(v).trim().replace(/^@/, "")}`,
    };
  }
  if (l.includes("facebook")) {
    return {
      key: "facebook",
      tooltip: "Facebook",
      icon: <FacebookIcon fontSize="small" />,
      buildUrl: (v) => `https://facebook.com/${String(v).trim().replace(/^@/, "")}`,
    };
  }
  if (l.includes("snapchat")) {
    return {
      key: "snapchat",
      tooltip: "Snapchat",
      icon: <FontAwesomeIcon icon={faSnapchatGhost} style={{ fontSize: 14 }} />,
      buildUrl: (v) => `https://snapchat.com/add/${String(v).trim().replace(/^@/, "")}`,
    };
  }

  if (l.includes("tiktok") || l.includes("tik tok")) {
    return {
      key: "tiktok",
      tooltip: "TikTok",
      icon: <FontAwesomeIcon icon={faTiktok} style={{ fontSize: 14 }} />,
      buildUrl: (v) => `https://tiktok.com/@${String(v).trim().replace(/^@/, "")}`,
    };
  }

  return null; 
};

const prettifySectionSubtitle = (sectionName = "") => {
  const s = sectionName.toLowerCase();
  if (s.includes("social")) return "Drop your handles so friends can find you.";
  if (s.includes("school")) return "Make matching feel more local.";
  if (s.includes("basic") || s.includes("profile"))
    return "Keep it simple + you.";
  if (s.includes("personal")) return "For authenticity, cannot be edited!";
  return "Quick edits — no long forms.";
};

const computeCompletion = (fields, sectionName, currentFormState) => {
  if (!fields?.length) return { filled: 0, total: 0 };
  let filled = 0;
  fields.forEach((field, idx) => {
    const fieldName = `${sectionName}-section-field-${idx}`;
    const v =
      currentFormState?.[sectionName]?.[fieldName] ?? field.defaultValue ?? "";
    if (String(v).trim().length > 0) filled += 1;
  });
  return { filled, total: fields.length };
};

const isPersonalSection = (sectionName = "") =>
  sectionName.toLowerCase().includes("personal");

// ---------- Section Card ----------
const SectionCard = ({
  sectionName,
  fields,
  editing,
  expanded,
  onToggleExpanded,
  currentFormState,
  handleInputChange,
  columns = 2,
}) => {
  const { filled, total } = computeCompletion(
    fields,
    sectionName,
    currentFormState
  );

  const commonSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "14px",
      backgroundColor: "rgba(0,0,0,0.22)",
      color: "white",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(255,255,255,0.14)",
    },
    "& .MuiInputLabel-root": {
      color: "rgba(255,255,255,0.70)",
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: "rgba(255,255,255,0.85)",
    },
    "& .Mui-disabled": {
      WebkitTextFillColor: "rgba(255,255,255,0.55)",
    },
    "& .MuiFormHelperText-root": {
      color: "rgba(255,255,255,0.55)",
    },
  };

  return (
    <Box
      sx={{
        borderRadius: "18px",
        border: "1px solid rgba(255,255,255,0.10)",
        background: "rgba(255,255,255,0.04)",
        backdropFilter: "blur(10px)",
        boxShadow: "0 12px 36px rgba(0,0,0,0.22)",
        overflow: "hidden",
      }}
    >
      {/* Header (tap to expand) */}
      <Box
        onClick={onToggleExpanded}
        sx={{
          px: 2,
          py: 1.6,
          cursor: "pointer",
          userSelect: "none",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontWeight: 900,
              letterSpacing: "0.2px",
              color: "white",
              fontSize: "1.02rem",
              lineHeight: 1.2,
            }}
          >
            {sectionName}
          </Typography>
          <Typography
            sx={{
              color: "rgba(255,255,255,0.65)",
              fontSize: "0.82rem",
              mt: 0.35,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: { xs: "72vw", sm: "520px" },
            }}
          >
            {prettifySectionSubtitle(sectionName)}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {(() => {
  const personal = isPersonalSection(sectionName);

  const chipKey = personal ? "locked" : editing ? "editing" : "progress";

  const chipBaseSx = {
    borderRadius: "10px",
    fontWeight: 900,
    color: "rgba(255,255,255,0.88)",
    border: "1px solid rgba(255,255,255,0.10)",
  };

  return (
    <Box sx={{ minWidth: 84, display: "flex", justifyContent: "flex-end" }}>
      <Box
        key={chipKey}
        sx={{
          animation: "chipPop 160ms ease",
          "@keyframes chipPop": {
            "0%": { opacity: 0, transform: "scale(0.96)" },
            "100%": { opacity: 1, transform: "scale(1)" },
          },
        }}
      >
        {personal ? (
          <Chip
            size="small"
            icon={
              <LockRoundedIcon
                fontSize="small"
                sx={{ color: "rgba(255,255,255,0.80)" }}
              />
            }
            label="Locked"
            sx={{
              ...chipBaseSx,
              backgroundColor: "rgba(255,255,255,0.08)",
            }}
          />
        ) : (
          <Chip
            size="small"
            label={editing ? "Editing" : `${filled}/${total}`}
            sx={{
              ...chipBaseSx,
              backgroundColor: editing
                ? "rgba(99,102,241,0.22)"
                : "rgba(255,255,255,0.08)",
            }}
          />
        )}
      </Box>
    </Box>
  );
})()}

          <ExpandMoreRoundedIcon
            sx={{
              color: "rgba(255,255,255,0.75)",
              transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 180ms ease",
            }}
          />
        </Box>
      </Box>

      <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

      {/* Fields */}
      <Collapse in={expanded} timeout={200} unmountOnExit>
        <Box sx={{ px: 2, py: 2 }}>
          <Grid container spacing={2}>
            {fields.map((field, fieldIndex) => {
              const { label, defaultValue, type, choices, required, editable } =
                field;

              const fieldName = `${sectionName}-section-field-${fieldIndex}`;
              const isDisabled = !editing || editable === false;
              const isRequired = required && editable !== false;

              const value =
                currentFormState?.[sectionName]?.[fieldName] ??
                defaultValue ??
                "";

              const socialCfg = isSocialFieldLabel(label)
                ? socialConfigForLabel(label)
                : null;

              const openSocial = () => {
                if (!socialCfg) return;
                const v = String(value || "").trim();
                if (!v) return;
                window.open(
                  socialCfg.buildUrl(v),
                  "_blank",
                  "noopener,noreferrer"
                );
              };

              const startAdornment = socialCfg ? (
                <InputAdornment position="start">
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 30,
                      height: 30,
                      borderRadius: "10px",
                      border: "1px solid rgba(255,255,255,0.12)",
                      backgroundColor: "rgba(0,0,0,0.22)",
                      color: "rgba(255,255,255,0.85)",
                    }}
                  >
                    {socialCfg.icon}
                  </Box>
                </InputAdornment>
              ) : null;

              const endAdornment =
                socialCfg && String(value || "").trim().length > 0 ? (
                  <InputAdornment position="end">
                    <Tooltip title={`Open ${socialCfg.tooltip}`} arrow>
                      <IconButton
                        onClick={openSocial}
                        size="small"
                        type="button"
                        sx={{
                          borderRadius: "10px",
                          border: "1px solid rgba(255,255,255,0.12)",
                          backgroundColor: "rgba(0,0,0,0.20)",
                          color: "rgba(255,255,255,0.85)",
                          "&:hover": {
                            backgroundColor: "rgba(255,255,255,0.10)",
                          },
                        }}
                      >
                        <LinkIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ) : null;

              const gridSize =
                columns === 1 ? { xs: 12 } : { xs: 12, sm: 6 };

              if (type === "text") {
                return (
                  <Grid item {...gridSize} key={fieldName}>
                    <TextField
                      fullWidth
                      required={isRequired}
                      disabled={isDisabled}
                      id={fieldName}
                      name={fieldName}
                      label={label}
                      variant="outlined"
                      value={value}
                      onChange={(e) => handleInputChange(e, sectionName)}
                      size="small"
                      placeholder={
                        socialCfg
                          ? `@${socialCfg.tooltip.toLowerCase()} handle`
                          : ""
                      }
                      sx={commonSx}
                      InputProps={{
                        startAdornment,
                        endAdornment,
                      }}
                    />
                  </Grid>
                );
              }

              if (type === "select") {
                return (
                  <Grid item {...gridSize} key={fieldName}>
                    <TextField
                      fullWidth
                      required={isRequired}
                      disabled={isDisabled}
                      id={fieldName}
                      name={fieldName}
                      label={label}
                      variant="outlined"
                      value={value}
                      onChange={(e) => handleInputChange(e, sectionName)}
                      size="small"
                      sx={commonSx}
                      select
                    >
                      {(choices || []).map((choice, idx) => (
                        <MenuItem key={idx} value={choice}>
                          {choice}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                );
              }

              return null;
            })}
          </Grid>
        </Box>
      </Collapse>
    </Box>
  );
};

SectionCard.propTypes = {
  sectionName: PropTypes.string.isRequired,
  fields: PropTypes.array.isRequired,
  editing: PropTypes.bool.isRequired,
  expanded: PropTypes.bool.isRequired,
  onToggleExpanded: PropTypes.func.isRequired,
  currentFormState: PropTypes.object,
  handleInputChange: PropTypes.func.isRequired,
  columns: PropTypes.oneOf([1, 2]),
};

// ---------- Main ----------
const MultiSectionForm = ({ SectionToFormFields, onFormsSave }) => {
  const [editing, setEditing] = useState(false);
  const [expandedSection, setExpandedSection] = useState(null);
  const [currentFormState, setCurrentFormState] = useState(null);

  const sectionToFormFieldsRef = useRef(null);

  const baselineRef = useRef(null);

  useEffect(() => {
    if (!SectionToFormFields) return;

    sectionToFormFieldsRef.current = SectionToFormFields;

    // If we already have local state, don't blow it away on prop changes.
    if (currentFormState && baselineRef.current) return;

    const defaults = buildDefaultState(SectionToFormFields);
    baselineRef.current = defaults;
    setCurrentFormState(defaults);

    const first = Object.keys(SectionToFormFields)[0] || null;
    setExpandedSection(first);
  }, [SectionToFormFields, currentFormState]);

  const hasChanges = useMemo(() => {
    if (!baselineRef.current || !currentFormState) return false;
    return JSON.stringify(baselineRef.current) !== JSON.stringify(currentFormState);
  }, [currentFormState]);

  const normalizeHandle = (raw) => {
    const v = String(raw ?? "").trim();
    if (!v) return "";
    // Remove spaces; keep it simple & predictable
    const noSpaces = v.replace(/\s+/g, "");
    return noSpaces.startsWith("@") ? noSpaces : `@${noSpaces}`;
  };  

  const handleInputChange = (event, sectionName) => {
    const { name, value } = event.target;
  
    const fieldName =
      name.split("-")[0] === sectionName ? name : `${sectionName}-${name}`;
  
    // Figure out which field this is (so we can detect socials)
    const fieldIndex = Number(String(fieldName).split("-section-field-")[1]);
    const fieldData = sectionToFormFieldsRef.current?.[sectionName]?.[fieldIndex];
  
    const isSocial = fieldData?.label ? isSocialFieldLabel(fieldData.label) : false;
    const nextValue = isSocial ? normalizeHandle(value) : value;
  
    setCurrentFormState((prev) => {
      const updatedSection = { ...(prev?.[sectionName] || {}) };
      updatedSection[fieldName] = nextValue;
  
      return {
        ...(prev || {}),
        [sectionName]: updatedSection,
      };
    });
  };  

  const handleToggleEdit = () => setEditing(true);

  const handleCancel = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();

    setCurrentFormState(baselineRef.current);
    setEditing(false);
  };

  const handleSave = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();

    try {
      if (!currentFormState || !sectionToFormFieldsRef.current) return;

      const formData = extractFormData(currentFormState, sectionToFormFieldsRef);
      onFormsSave(formData);

      baselineRef.current = currentFormState;

      setEditing(false);
    } catch (e) {
      console.error("Profile save failed:", e);
    }
  };

  if (!currentFormState) return null;

  return (
    <Box sx={{ width: "100%" }}>
      {/* Header row */}
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          flexDirection: { xs: "column", sm: "row" },
          gap: 1,
          mb: 2,
        }}
      >
        <Box>
          <Typography
            sx={{
              color: "white",
              fontWeight: 950,
              letterSpacing: "0.2px",
              fontSize: "1.15rem",
            }}
          >
            Profile Info
          </Typography>
          <Typography
            sx={{ color: "rgba(255,255,255,0.65)", fontSize: "0.85rem" }}
          >
            See or add more details about yourself
          </Typography>
        </Box>

        {!editing ? (
          <Button
            onClick={handleToggleEdit}
            variant="contained"
            startIcon={<EditRoundedIcon />}
            sx={{
              borderRadius: "14px",
              textTransform: "none",
              fontWeight: 950,
              px: 2,
              background:
                "linear-gradient(135deg, #5c6bc0 0%, #3f51b5 30%, #303f9f 70%)",
              boxShadow: "0 10px 30px rgba(63,81,181,0.35)",
              "&:hover": {
                background:
                  "linear-gradient(135deg, #6f7bd1 0%, #4b5fc6 55%, #3647a5 100%)",
              },
            }}
            type="button"
          >
            Edit
          </Button>
        ) : null}
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {Object.keys(SectionToFormFields).map((sectionName) => {
          const isSocial =
            sectionName.toLowerCase().includes("social") ||
            SectionToFormFields[sectionName]?.some((f) =>
              isSocialFieldLabel(f.label)
            );

          return (
            <SectionCard
              key={sectionName}
              sectionName={sectionName}
              fields={SectionToFormFields[sectionName]}
              editing={editing}
              currentFormState={currentFormState}
              handleInputChange={handleInputChange}
              expanded={expandedSection === sectionName}
              onToggleExpanded={() =>
                setExpandedSection((prev) =>
                  prev === sectionName ? null : sectionName
                )
              }
              columns={isSocial ? 1 : 2}
            />
          );
        })}
      </Box>

      {/* Sticky actions */}
      {editing && (
        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            mt: 2,
            pt: 2,
            pb: 1,
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
            background:
              "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(10,12,18,0.85) 35%, rgba(10,12,18,0.95) 100%)",
            backdropFilter: "blur(10px)",
          }}
        >
          <Button
            variant="outlined"
            onClick={handleCancel}
            startIcon={<CloseRoundedIcon />}
            sx={{
              borderRadius: "14px",
              textTransform: "none",
              fontWeight: 950,
              borderColor: "rgba(255,255,255,0.22)",
              color: "rgba(255,255,255,0.85)",
            }}
            type="button"
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            type="button"
            onClick={handleSave}
            disabled={!hasChanges}
            startIcon={<SaveRoundedIcon />}
            sx={{
              borderRadius: "14px",
              textTransform: "none",
              fontWeight: 950,
              px: 2,
              background: hasChanges
                ? "linear-gradient(135deg, rgba(34,197,94,0.95), rgba(99,102,241,0.9))"
                : "rgba(255,255,255,0.16)",
            }}
          >
            Save
          </Button>
        </Box>
      )}
    </Box>
  );
};

MultiSectionForm.propTypes = {
  SectionToFormFields: PropTypes.object.isRequired,
  onFormsSave: PropTypes.func.isRequired,
};

export default MultiSectionForm;
